/**
 * AutoCorrectness — inline compliance panel powered by /api/auto/correctness.
 *
 * Drop it in any form-heavy page and pass:
 *   payload      — the JSON we send to the checker (usually the form's
 *                  current values)
 *   schema_hint  — "lease" | "invoice" | "rent-increase-notice" | …
 *   province     — 2-letter province code
 *   debounceMs   — how long to wait after last change before re-checking
 *                  (default 700ms). Set to 0 to disable auto-run and only
 *                  refresh via the "Re-check" button.
 *
 * Displays a compact roll-up (X errors / Y warnings) plus expandable
 * issue list with statute citation + suggested_fix per issue.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ShieldCheck, Loader2, AlertCircle, X, Check, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoCorrectness({
  payload,
  schema_hint,
  province,
  debounceMs = 700,
  compact = false,
  className = "",
}) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  // Memoize the payload signature so identical values don't refire the check.
  const sig = useMemo(() => {
    try { return JSON.stringify({ payload, schema_hint, province }); } catch { return ""; }
  }, [payload, schema_hint, province]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auto/correctness`, { payload, schema_hint, province });
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Correctness check failed." });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!payload || debounceMs === 0) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(run, debounceMs);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, debounceMs]);

  const errors = result?.errors ?? 0;
  const warnings = result?.warnings ?? 0;
  const total = (result?.issues || []).length;
  const status = errors > 0 ? "error" : warnings > 0 ? "warn" : total === 0 && result ? "ok" : "idle";

  const dotClass =
    status === "error" ? "bg-rose-500"
    : status === "warn" ? "bg-amber-500"
    : status === "ok" ? "bg-emerald-500"
    : "bg-slate-300";
  const label =
    status === "error" ? `${errors} error${errors === 1 ? "" : "s"}` +
                          (warnings ? ` · ${warnings} warning${warnings === 1 ? "" : "s"}` : "")
    : status === "warn" ? `${warnings} warning${warnings === 1 ? "" : "s"}`
    : status === "ok" ? "All checks passed"
    : "Auto correctness";

  return (
    <div
      className={`rounded-xl border ${
        status === "error" ? "border-rose-200 bg-rose-50/60"
        : status === "warn" ? "border-amber-200 bg-amber-50/60"
        : status === "ok" ? "border-emerald-200 bg-emerald-50/40"
        : "border-slate-200 bg-white"
      } ${className}`}
      data-testid="auto-correctness"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left"
        data-testid="auto-correctness-toggle"
      >
        <div className={`shrink-0 w-2.5 h-2.5 rounded-full ${dotClass} ${busy ? "animate-pulse" : ""}`}/>
        <ShieldCheck className="h-4 w-4 text-slate-600 shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold leading-none">Auto correctness</div>
          <div className={`text-sm font-semibold mt-0.5 ${
            status === "error" ? "text-rose-800"
            : status === "warn" ? "text-amber-800"
            : status === "ok" ? "text-emerald-800"
            : "text-slate-700"
          }`}>
            {busy ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin"/> Checking…</span> : label}
          </div>
        </div>
        {total > 0 && (open ? <ChevronUp className="h-4 w-4 text-slate-400"/> : <ChevronDown className="h-4 w-4 text-slate-400"/>)}
      </button>

      {!compact && open && total > 0 && (
        <ul className="border-t divide-y divide-slate-100" data-testid="auto-correctness-list">
          {result.issues.map((i, idx) => (
            <li key={idx} className="flex items-start gap-3 px-3.5 py-3" data-testid={`auto-correctness-issue-${idx}`}>
              <div className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white ${
                i.severity === "error" ? "bg-rose-600" : i.severity === "warning" ? "bg-amber-500" : "bg-slate-400"
              }`}>
                {i.severity === "error" ? <X className="h-3 w-3"/> : <AlertCircle className="h-3 w-3"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-slate-900">{i.field ? `${i.field}: ` : ""}{i.message}</div>
                {i.suggested_fix && (
                  <div className="text-[11px] text-emerald-800 mt-1 p-1.5 rounded bg-emerald-50 border border-emerald-100">
                    <span className="font-semibold">Fix: </span>{i.suggested_fix}
                  </div>
                )}
                {i.statute && (
                  <div className="text-[11px] text-slate-500 mt-1 italic">Statute: {i.statute}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
